import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { useToast } from '../shared/components/ToastProvider';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';
import { CERT_TYPE_LABELS, GAS_CLASSIFICATION_LABELS } from '../data/mappers';
import { pbEngineer } from '../lib/pb';
import { portalApi } from '../lib/api';
import CertificateWizard from './certificates/CertificateWizard';

export default function EngineerCertificates() {
  const { data, refresh } = useData();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [priorYearCertificate, setPriorYearCertificate] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);

  // Engineers get their own collections — the shared DataProvider loads
  // only tenant branding for engineer sessions, so certificates must come
  // from pbEngineer (list rule: engineer's own or job-assigned).
  const loadCertificates = useCallback(async () => {
    try {
      setLoading(true);
      const records = await pbEngineer.collection('certificates').getFullList({
        sort: '-created',
        expand: 'client_id',
      });
      setCertificates(records);
    } catch (err) {
      console.error('Failed to load certificates:', err);
      notify('Failed to load certificates', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  const filtered = certificates
    .filter((cert) => {
      const typeMatch = filter === 'all' || cert.type === filter;
      const searchMatch = !searchTerm ||
        cert.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (CERT_TYPE_LABELS[cert.type] || cert.type).toLowerCase().includes(searchTerm.toLowerCase());
      return typeMatch && searchMatch;
    })
    .sort((a, b) => new Date(b.created) - new Date(a.created));

  const getTypeColor = (type) => {
    if (type === 'lgsr') return 'danger';
    if (type === 'gsr') return 'warning';
    if (type === 'gwn') return 'error';
    return 'muted';
  };

  const handleCreateNew = () => {
    setSelectedType(null);
    setSelectedJob(null);
    setPriorYearCertificate(null);
    setWizardOpen(true);
  };

  // Find a prior certificate for the same client/property (LGSR renewals
  // rarely change appliance lists — UX spec §4 desktop pre-fill).
  const findPriorYear = (type, clientId) => {
    if (!clientId) return null;
    const prior = certificates
      .filter((c) => c.type === type && c.client_id === clientId && c.id)
      .sort((a, b) => new Date(b.created) - new Date(a.created))[0];
    return prior || null;
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    // Engineer sessions have no jobs in DataProvider — fetch assigned jobs
    // directly so the wizard can link the certificate to a job.
    pbEngineer.collection('engineer_jobs').getFullList({
      sort: '-created',
      filter: 'status != "completed"',
    }).then((jobs) => {
      const first = jobs[0] || null;
      setSelectedJob(first);
      setPriorYearCertificate(findPriorYear(type, first?.client_id));
    }).catch(() => {
      setSelectedJob(null);
      setPriorYearCertificate(null);
    });
    setWizardOpen(true);
  };

  const handleViewPdf = async (cert) => {
    try {
      setPreviewingId(cert.id);
      const blob = await portalApi.fetchCertificatePdf(cert.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      notify(err.message || 'Failed to generate PDF', 'error');
    } finally {
      setPreviewingId(null);
    }
  };

  const handleRefresh = async () => {
    await refresh();
    await loadCertificates();
    notify('Certificates refreshed', 'success');
  };

  const gasCertTypes = [
    { value: 'lgsr', label: 'Landlord Gas Safety (CP12)', icon: Icons.Shield },
    { value: 'gsr', label: 'Gas Safety Record', icon: Icons.CheckCircle },
    { value: 'gsirep', label: 'Safety Inspection Report', icon: Icons.FileSearch },
    { value: 'gwn', label: 'Warning Advice Notice', icon: Icons.AlertTriangle },
    { value: 'gmsc', label: 'Service Maintenance Checklist', icon: Icons.Settings },
    { value: 'gbsr', label: 'Breakdown/Servicing Record', icon: Icons.Wrench },
    { value: 'wpcr', label: 'Pipework Commissioning Record', icon: Icons.Pipe }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gas Safety Certificates</h1>
        <div className="header-actions">
          <button className="secondary-button" onClick={handleRefresh} disabled={loading}>
            <SafeIcon icon={Icons.RefreshCw} size={18} />
            Refresh
          </button>
          <button className="primary-button" onClick={handleCreateNew}>
            <SafeIcon icon={Icons.Plus} size={18} />
            New Certificate
          </button>
        </div>
      </div>

      {/* Quick type selection */}
      <div className="quick-type-selection">
        {gasCertTypes.map(({ value, label, icon }) => (
          <button
            key={value}
            className={`type-button ${selectedType === value ? 'selected' : ''}`}
            onClick={() => handleTypeSelect(value)}
            aria-label={`Create ${label}`}
          >
            <SafeIcon icon={icon} size={20} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Filters and search */}
      <div className="filter-bar">
        <div className="search-input">
          <SafeIcon icon={Icons.Search} size={16} />
          <input
            type="text"
            placeholder="Search certificates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search certificates"
          />
        </div>

        <div className="filter-buttons">
          {['all', 'lgsr', 'gsr', 'gwn', 'gsirep', 'gmsc', 'gbsr', 'wpcr'].map((type) => (
            <button
              key={type}
              className={`filter-button ${filter === type ? 'active' : ''}`}
              onClick={() => setFilter(type)}
            >
              {type === 'all' ? 'All' : CERT_TYPE_LABELS[type] || type}
            </button>
          ))}
        </div>
      </div>

      {/* Certificates grid */}
      {loading ? (
        <div className="skeleton-container">
          {[1, 2, 3].map((i) => (
            <div key={i} className="certificate-skeleton skeleton-card" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <SafeIcon icon={Icons.FileText} size={48} />
          <h3>No certificates found</h3>
          <p>{filter === 'all'
            ? "You haven't created any certificates yet. Select a certificate type to get started."
            : `No ${CERT_TYPE_LABELS[filter] || filter} certificates found`}
          </p>
          <button className="primary-button" onClick={handleCreateNew}>
            <SafeIcon icon={Icons.Plus} size={18} />
            Create First Certificate
          </button>
        </div>
      ) : (
        <div className="certificates-grid">
          {filtered.map((cert) => {
            const body = (() => {
              try {
                return typeof cert.form_data === 'string' ? JSON.parse(cert.form_data) : (cert.form_data || {});
              } catch {
                return {};
              }
            })();
            return (
              <div
                key={cert.id}
                className="certificate-card"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`${cert.id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`${cert.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header">
                  <div className={`card-type type-${getTypeColor(cert.type)}`}>
                    {CERT_TYPE_LABELS[cert.type] || cert.type}
                  </div>
                  <span className="card-date">
                    {new Date(cert.created).toLocaleDateString()}
                  </span>
                </div>

                {cert.certificate_number && (
                  <h3 className="card-number">{cert.certificate_number}</h3>
                )}

                {body.property_address && (
                  <p className="card-address">
                    <SafeIcon icon={Icons.MapPin} size={14} />
                    {body.property_address}
                  </p>
                )}

                {cert.expiry_date && (
                  <div className="card-expiry">
                    <SafeIcon icon={Icons.Calendar} size={14} />
                    <span>Expires: {new Date(cert.expiry_date).toLocaleDateString()}</span>
                  </div>
                )}

                {cert.classification && (
                  <div className={`classification-badge classification-${cert.classification}`}>
                    {GAS_CLASSIFICATION_LABELS[cert.classification] || cert.classification}
                  </div>
                )}

                {Array.isArray(body.appliances) && body.appliances.length > 0 && (
                  <div className="card-appliances">
                    <SafeIcon icon={Icons.Zap} size={14} />
                    <span>{body.appliances.length} appliance(s)</span>
                  </div>
                )}

                <div className="card-actions">
                  <button
                    type="button"
                    className="secondary-button card-pdf-button"
                    onClick={(e) => { e.stopPropagation(); handleViewPdf(cert); }}
                    disabled={previewingId === cert.id}
                    aria-label={`Preview PDF for ${cert.certificate_number || 'certificate'}`}
                  >
                    <SafeIcon icon={Icons.FileText} size={16} />
                    {previewingId === cert.id ? 'Generating...' : 'PDF Preview'}
                  </button>
                  <SafeIcon icon={Icons.ChevronRight} size={18} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Certificate Wizard Overlay */}
      {wizardOpen && (
        <CertificateWizard
          certificateType={selectedType || 'lgsr'}
          jobId={selectedJob?.id}
          engineerJob={selectedJob}
          client={selectedJob ? { id: selectedJob.client_id, name: selectedJob.client?.name || '' } : undefined}
          priorYearCertificate={priorYearCertificate}
          onSave={() => {
            setWizardOpen(false);
            loadCertificates();
          }}
          onCancel={() => setWizardOpen(false)}
        />
      )}
    </div>
  );
}
